import { SUMMARY_SEPARATOR } from "@/lib/buildSummary";

// Live adapter: mirror rows (repos/AgenticOS/frontend/src/data/breakageBoard.json) → existing
// WatchTile contract (slug/name/brand/state/line/number/label/stamp/track). Council wording table
// drives every label; plain English only — no job names, paths, or file names on the board face.
const KIND_WORD = {
  tokens: "Keeps sign-ins working",
  "script-bug": "Keeps the dashboards fresh",
  "data-access": "Watches the outreach queue",
  runtime: "Keeps weekly checkups running",
  "runtime-gap": "Nightly pattern review",
  "dead-path": "Auto-repair helper",
  "lock-contention": "Watches social replies",
  "missing-dep": "Watches public reviews",
};

const hhmm = (iso, fallback) => {
  if (!iso) return fallback;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return fallback;
  return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const shortDate = (iso) => {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "a while" : t.toLocaleDateString([], { month: "short", day: "numeric" });
};

// severity → board state (wording table): signal-firing=amber alert; tool-broken=red broken;
// waiting-on-jeff=amber waiting; annotated-but-green-work=healthy; unannotated=amber stale ("Needs sorting").
const tileState = (card) => {
  if (!card.annotated) return "stale"; // "Needs sorting — Kit decides what this is"
  if (card.kind === "tokens" && card.owner === "jeff") return "waiting";
  if (card.severity === "green-work") return "healthy";
  if (card.kind === "runtime-gap" || card.kind === "lock-contention") return "alert";
  return "broken";
};

const line = (card, state) => {
  const when = hhmm(card.failedAt || card.lastRun, "the last run");
  if (state === "stale") return "Needs sorting — Kit decides what this is";
  if (state === "waiting") return `Waiting on you: one click to renew the sign-in`;
  if (state === "alert") return `Alert fired — condition met (${when})`;
  if (state === "healthy" && card.annotated && card.problem) return "Did its job — Kit still owes it a small fix";
  return `Failed last run (${when})`;
};

// last 15 runs, oldest → newest: mostly clean with the recent failures the mirror saw
const TRACK_LEN = 15;
const buildRuns = (streak, newest) => {
  const fail = newest || "failed";
  const past = Array(Math.max(0, TRACK_LEN - Math.min(14, streak))).fill("clean");
  const broken = Array(TRACK_LEN - past.length - 1).fill("missed");
  return [...past, ...broken, fail];
};

export const toTiles = (module, annotationsNote) => {
  const cards = module?.cards || [];
  const tiles = cards.map((c) => {
    const state = tileState(c);
    const when = hhmm(c.failedAt || c.lastRun, "recently");
    return {
      slug: c.id,
      name: KIND_WORD[c.kind] || c.name || "Keeps a routine working",
      brand: "Across the portfolio",
      state,
      line: line(c, state),
      number: String(c.streak || 1),
      label: c.streak > 1 ? "runs failed in a row" : "failed run",
      stamp: `${state === "alert" ? "fired" : "failed"} ${when}`,
      track: buildRuns(c.streak || 1, state === "broken" ? "failed" : state === "alert" ? "flagged" : "missed"),
      detail: {
        does: KIND_WORD[c.kind] || "Keeps a routine running",
        why: c.problem || "Its last run hit a problem and stopped — Kit is on it.",
        lastFailed: c.failedAt || c.lastRun ? when : "not yet recorded",
        kit: c.fix || "Kit works down the fix list — nothing needs Jeff here.",
      },
      problem: c.problem,
      fix: c.fix,
      jobName: c.name,
    };
  });
  return tiles;
};

// Hygiene tile (count-only, from the ingest) + stall tile ride after the problem tiles.
export const extraTiles = (module) => {
  const counts = module?.counts || {};
  const out = [];
  if (counts.stalled > 0) {
    out.push({
      slug: "stalled-work",
      name: "Work waiting on a decision",
      brand: "Across the portfolio",
      state: "waiting",
      line: `${counts.stalled} spot${counts.stalled === 1 ? "" : "s"} in the log marked waiting on you`,
      number: String(counts.stalled),
      label: "waiting on you",
      stamp: "from Kit's work log",
      track: "",
      detail: {
        does: "Collects the decisions Kit is waiting on",
        why: "Some work items sit parked until Jeff makes the call.",
        lastFailed: "from Kit's work log",
        kit: "Say the word on any of them and they move.",
      },
      problem: "Some work items sit parked until Jeff makes the call.",
      fix: "Say the word on any of them and they move.",
    });
  }
  out.push({
    slug: "hygiene",
    name: "Files that need re-homing",
    brand: "Across the workspace",
    state: counts.hygiene > 0 ? "stale" : "healthy",
    line: counts.hygiene > 0 ? `${counts.hygiene} files could be in a better home` : "No files out of place right now",
    number: String(counts.hygiene),
    label: "files to re-home",
    stamp: module?.stamp ? `checked ${module.stamp.ingested_at?.slice(0, 10)}` : "",
    track: "",
    detail: {
      does: "Keeps every brand's files in their right folders",
      why: "The tidy-up lane counts files living outside their brand folders.",
      lastFailed: module?.stamp ? `counted ${module.stamp.ingested_at?.slice(0, 10)}` : "not yet recorded",
      kit: "Kit re-homes them on the next tidy-up pass.",
    },
    problem: "The tidy-up lane counts files living outside their brand folders.",
    fix: "Kit re-homes them on the next tidy-up pass.",
  });
  return out;
};

export const buildLiveSummary = (tiles) => {
  const count = (state) => tiles.filter((t) => t.state === state).length;
  const broken = count("broken");
  const alert = count("alert");
  const waiting = count("waiting");
  const healthy = count("healthy");
  const sorting = count("stale");
  const parts = [{ key: "broken", text: `${broken} broken`, tone: broken ? "red" : "neutral" }];
  if (alert) parts.push({ key: "alert", text: `${alert} ${alert === 1 ? "alert" : "alerts"} firing`, tone: "amber" });
  if (waiting) parts.push({ key: "waiting", text: `${waiting} waiting on you`, tone: "amber" });
  if (sorting) parts.push({ key: "sorting", text: `${sorting} needs sorting`, tone: "amber" });
  if (healthy && healthy === tiles.length) parts.push({ key: "green", text: "everything green", tone: "green" });
  const tone = broken ? "red" : alert || waiting || sorting ? "amber" : healthy === tiles.length ? "quiet" : "neutral";
  return { parts, tone, text: parts.map((p) => p.text).join(SUMMARY_SEPARATOR) };
};